import React from 'react';

// Las dos manos con la palma hacia ti: la izquierda a la izquierda, con el pulgar hacia el
// centro. GeoVision numera los dedos 0-4 (izquierda, del pulgar al meñique) y 5-9 (derecha).
// La huella se ve donde va: en la yema de cada dedo.

// Mano izquierda en su espacio (0 0 150 172). Cada dedo: base (x, y), ancho, largo y giro
// en grados (+ = la punta se va a la derecha). La derecha es la misma, en espejo.
const DEDOS = [
    { x: 109, y: 120, ancho: 22, largo: 54, giro: 40 },     // pulgar
    { x: 97, y: 82, ancho: 18, largo: 60, giro: 6 },        // índice
    { x: 77, y: 80, ancho: 18.5, largo: 68, giro: 0 },      // medio
    { x: 57, y: 82, ancho: 18, largo: 61, giro: -5 },       // anular
    { x: 38.5, y: 90, ancho: 16, largo: 46, giro: -12 },    // meñique
];
// Del índice baja una curva suave hasta el pulgar (el pliegue entre los dos), sin hombro
const PALMA = 'M31 92 C29 80 37 72 47 71 L101 71 C105 72 106 76 106 82 C106 92 109 97 114.8 96 L118 108 C119 118 117 126 113 134 C107 146 101 154 99 166 L50 166 C47 152 38 142 33 127 C29 115 29 104 31 92 Z';

// El dedo es una cápsula que entra un poco en la palma, así la mano queda de una pieza
const Capsula = ({ d, className }) => (
    <rect x={d.x - d.ancho / 2} y={d.y - d.largo} width={d.ancho} height={d.largo + 12} rx={d.ancho / 2} className={className} />
);
const girar = (d) => `rotate(${d.giro} ${d.x} ${d.y})`;

// Yema: con huella guardada, cyan con sus crestas; pendiente, punteada; vacía, gris
const Yema = ({ d, estado }) => {
    const cy = d.y - d.largo + d.ancho * 0.62;
    const rx = d.ancho * 0.3;
    const ry = d.ancho * 0.42;
    if (estado === 'enrolado') {
        return (
            <>
                <ellipse cx={d.x} cy={cy} rx={rx} ry={ry} fill="#22D3EE" stroke="#0891B2" strokeWidth="1" />
                {[0.35, 0.62, 0.88].map((k) => (
                    <path
                        key={k} d={`M${d.x - rx * k} ${cy + ry * 0.35} A${rx * k} ${ry * k} 0 0 1 ${d.x + rx * k} ${cy + ry * 0.35}`}
                        fill="none" stroke="#FFFFFF" strokeWidth="0.9" strokeLinecap="round"
                    />
                ))}
            </>
        );
    }
    return (
        <ellipse
            cx={d.x} cy={cy} rx={rx} ry={ry}
            fill={estado === 'pendiente' ? '#ECFEFF' : '#E2E8F0'} stroke={estado === 'pendiente' ? '#0891B2' : '#CBD5E1'}
            strokeWidth="1" strokeDasharray={estado === 'pendiente' ? '2 1.5' : undefined}
        />
    );
};

const ESTADO_TEXTO = { enrolado: 'huella guardada', pendiente: 'pendiente en el lector', vacio: 'sin huella' };

const Mano = ({ lado, dedos, elegido, onElegir, deshabilitada }) => {
    const base = lado === 'derecha' ? 5 : 0;
    const espejo = lado === 'derecha' ? 'translate(150 0) scale(-1 1)' : undefined;
    return (
        <svg viewBox="0 0 150 172" className="w-full max-w-[170px] h-auto" role="group" aria-label={`Mano ${lado}`}>
            <g transform={espejo}>
                {/* Silueta: primero todo con borde y encima todo sin borde, así solo queda el contorno de afuera */}
                <g fill="#F8FAFC" stroke="#94A3B8" strokeWidth="3">
                    {DEDOS.map((d) => <g key={d.x} transform={girar(d)}><Capsula d={d} /></g>)}
                    <path d={PALMA} />
                </g>
                <g fill="#F8FAFC">
                    {DEDOS.map((d) => <g key={d.x} transform={girar(d)}><Capsula d={d} /></g>)}
                    <path d={PALMA} />
                </g>
                <g fill="none" stroke="#CBD5E1" strokeWidth="1.2" strokeLinecap="round">
                    <path d="M44 104 C60 98 80 98 100 104" />
                    <path d="M58 122 C72 116 90 118 104 112" />
                </g>
                {DEDOS.map((d, i) => {
                    const dedo = dedos[base + i];
                    const estado = dedo?.estado || 'vacio';
                    const activo = elegido === base + i;
                    return (
                        <g
                            key={d.x} transform={girar(d)}
                            className={`dedo-mano ${activo ? 'elegido' : ''} ${deshabilitada ? 'deshabilitado' : ''}`}
                            role="button" tabIndex={deshabilitada ? -1 : 0} aria-pressed={activo}
                            aria-label={`${dedo?.nombre}: ${ESTADO_TEXTO[estado]}`}
                            onClick={() => !deshabilitada && onElegir(base + i)}
                            onKeyDown={(ev) => { if (!deshabilitada && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); onElegir(base + i); } }}
                        >
                            <Capsula d={d} className="dedo-contorno" />
                            <Yema d={d} estado={estado} />
                        </g>
                    );
                })}
            </g>
        </svg>
    );
};

const Manos = ({ dedos, elegido, onElegir, deshabilitada }) => (
    <div className="grid grid-cols-2 gap-6 items-end">
        {['izquierda', 'derecha'].map((lado) => (
            <div key={lado} className="flex flex-col items-center">
                <Mano lado={lado} dedos={dedos} elegido={elegido} onElegir={onElegir} deshabilitada={deshabilitada} />
                <span className="text-xs text-slate-500 mt-2 capitalize">{lado}</span>
            </div>
        ))}
    </div>
);

export default Manos;
